import React, { useCallback } from "react";

import { RunIgnoredTrigger, VcsProvider } from "types/generated";
import { Commit } from "components/icons/generated";
import Counter from "ds/components/Counter";
import MetaInfoListItem from "components/MetaInfoList/Item";
import useTypedContext from "hooks/useTypedContext";
import { StackContext } from "views/Stack/Context";
import Box from "ds/components/Box";
import { useToggle } from "hooks/useToggle";
import { getCommitDigitsCount } from "utils/commit";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import CardCollapsible from "ds/components/Card/Collapsible";
import CardTitle from "ds/components/Card/components/Title";

import styles from "./styles.module.css";
import StackIgnoredRunsGroupCommitAuthorLink from "./CommitAuthorLink";

type StackIgnoredRunsGroupProps = {
  run: RunIgnoredTrigger;
  provider: VcsProvider;
  count: number;
  isOpen?: boolean;
  children?: React.ReactNode;
};

const StackIgnoredRunsGroup = (props: StackIgnoredRunsGroupProps) => {
  const { run, provider, children, count, isOpen = false } = props;
  const [isCollapsed, toggle] = useToggle(!isOpen);

  const { stack } = useTypedContext(StackContext);

  const handleToggle = useCallback(() => {
    toggle();
  }, [toggle]);

  return (
    <CardCollapsible
      fullbleed
      headerAlign="start"
      ariaLevel={3}
      ariaLabel={run.commit.message}
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      title={
        <Box direction="column" gap="medium" fullWidth>
          <Box gap="medium" gridArea="commit" align="center">
            <CardTitle tag="span">{run.commit.message}</CardTitle>
            <Counter count={count} />
          </Box>

          <Box gridArea="meta" gap="large">
            <TimestampMetaInfo
              prefix="Committed on"
              timestamp={run.commit.timestamp}
              postfix={
                <>
                  by&nbsp;
                  <StackIgnoredRunsGroupCommitAuthorLink
                    login={run.commit.authorLogin || undefined}
                    name={run.commit.authorName}
                    provider={provider}
                  />
                </>
              }
            />

            <MetaInfoListItem
              href={run.commit.url}
              linkText={run.commit.hash.slice(0, getCommitDigitsCount(provider))}
              icon={Commit}
              target="_blank"
              tooltip={stack.trackedCommit?.hash === run.commit.hash ? "Tracked commit" : null}
            />
          </Box>
        </Box>
      }
    >
      <div className={styles.wrapper}>{children}</div>
    </CardCollapsible>
  );
};

export default StackIgnoredRunsGroup;
